<template>
  <v-container>
    <v-row>
      <v-col cols="9">
        <v-skeleton-loader
          :loading="collectionPointLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <CollectionPointHeaderCard
            title="Výjimka z fakturace"
            detailType="billingException"
            :data="collectionPoint"
          />
        </v-skeleton-loader>

        <v-skeleton-loader
          :loading="collectionPointLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <v-card class="elevation-12">
            <v-card-text class="px-9">
              <ValidationObserver
                ref="billingExceptionForm"
                v-slot="{ passes }"
              >
                <v-form rel="billingExceptionForm" @submit.prevent="passes(save)">
                  <v-row>
                    <v-col>
                      <v-row>
                        <VDatePickerWithTextField
                          :disabled="datePickerLoading"
                          :loading="datePickerLoading"
                          :rules="{
                            'dateFormat': true,
                            'required': true,
                            'startDateAfter': minForStartDateFormatted,
                          }"
                          v-model="billingException.startDate"
                          label="Požadovaný termín zahájení"
                          :min="minForStartDateFormatted"
                          :max="maxForStartDateFormatted"
                          :allowed-dates="getAllowedDatesForBillingException"
                          menuAttach=".detail-datepicker--billing-exception"
                          cols="8"
                          colClass="py-0 detail-datepicker detail-datepicker--billing-exception"
                          data-cy="startDate"
                        />
                      </v-row>

                      <v-row>
                        <v-col cols="8" class="py-0">
                          <VAutocompleteWithValidation
                            rules="required"
                            outlined
                            dense
                            label="Požadovaný termín ukončení"
                            v-model="billingExceptionLength"
                            :items="requestedLengths"
                            item-text="localizedText"
                            item-value="name"
                            :loading="requestedLengthsLoading"
                            @change="setEndDate"
                            data-cy="billingExceptionLength"
                            return-object
                          />
                        </v-col>

                        <v-col
                          class="py-2"
                          v-if="endDate"
                        >
                          {{ formatDate(endDate) }}
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="8" class="py-0">
                          <VTextFieldWithValidation
                            v-model="billingException.phone"
                            rules="required|phone"
                            outlined
                            dense
                            label="Telefon (ve formátu +420222444666)"
                            data-cy="phone"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="8" class="py-0">
                          <VTextFieldWithValidation
                            rules="email"
                            v-model="billingException.email"
                            outlined
                            dense
                            label="E-mail"
                            data-cy="email"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col class="py-0">
                          <v-checkbox
                            label="Výjimka z překročení RK"
                            v-model="
                              billingException.exceedingReservedCapacity
                            "
                          >
                          </v-checkbox>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col class="py-0">
                          <v-checkbox
                            label="Výjimka z nedodržení účiníku"
                            v-model="
                              billingException.exceedingPowerFactor
                            "
                          >
                          </v-checkbox>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col class="py-0">
                          <v-checkbox
                            label="Výjimka za nevyžádanou dodávku jalové energie"
                            v-model="
                              billingException.reactivePowerSupply
                            "
                          >
                          </v-checkbox>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col class="py-0">
                          <v-checkbox
                            label="Výjimka z překročení RP"
                            v-model="
                              billingException.exceedingReservedPower
                            "
                          >
                          </v-checkbox>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="8" class="py-0">
                          <VFileInputWithValidation
                            v-model="billingExceptionFile"
                            :rules="{
                              required: false,
                              ext: ['pdf'],
                            }"
                            prepend-icon=""
                            append-icon="$file"
                            @click:append="
                              $event.target.offsetParent
                              .querySelector(`[class='v-file-input__text']`).click()
                            "
                            :clearable="false"
                            outlined
                            dense
                            label="Příloha .PDF"
                            accept=".pdf"
                            @change="fileUpload($event)"
                            data-cy="billing-exception-file"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" class="py-0">
                          <v-textarea
                            v-model="billingException.note"
                            maxLength="3000"
                            outlined
                            dense
                            label="Poznámka"
                            data-cy="note"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col>
                          <v-btn
                            depressed
                            type="submit"
                            height="40px"
                            min-width="125px"
                            color="primary"
                            :loading="loading"
                          >
                            Odeslat
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-form>
              </ValidationObserver>
            </v-card-text>
          </v-card>
        </v-skeleton-loader>
      </v-col>

      <CollectionPointRightSideCards
        :collectionPoint="collectionPoint"
        @searchInstallation="searchInstallation"
        @installationLoading="collectionPointLoading = $event"
        :eanErrorMessage="eanErrorMessage"
        :collectionPointLoading="collectionPointLoading"
      />
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import CollectionPointHeaderCard from '@/components/CollectionPointHeaderCard.vue';
import { installationMixin } from '@/components/mixins/installationMixin';
import CollectionPointRightSideCards from '@/components/utils/CollectionPointRightSideCards.vue';
import { onInstallationSaveSuccess } from '@/components/mixins/saveSuccess';
import VDatePickerWithTextField from '@/components/VDatePickerWithTextField.vue';
import parseDate from '@/utils/parseDate';

export default {
  name: 'BillingExceptionDetail',
  mixins: [installationMixin, onInstallationSaveSuccess],
  components: {
    CollectionPointHeaderCard,
    CollectionPointRightSideCards,
    VDatePickerWithTextField,
  },
  data() {
    return {
      datePickerLoading: false,
      requestedLengths: [],
      billingException: {
        email: '',
        phone: null,
        startDate: null,
        billingExceptionLength: 'THREE_MONTHS',
        note: null,
        exceedingReservedCapacity: false,
        exceedingPowerFactor: false,
        reactivePowerSupply: false,
        exceedingReservedPower: false,
      },
      billingExceptionFile: null,
      billingExceptionLength: null,
      loading: false,
      availableDates: [],
      requestedLengthsLoading: false,
      endDate: null,
    };
  },
  watch: {
    'billingException.startDate': {
      handler() {
        this.setEndDate();
      },
    },
    $route: {
      handler() {
        this.billingException = {
          email: '',
          phone: null,
          startDate: null,
          billingExceptionLength: 'THREE_MONTHS',
          note: null,
          exceedingReservedCapacity: false,
          exceedingPowerFactor: false,
          reactivePowerSupply: false,
          exceedingReservedPower: false,
        };
        this.billingExceptionFile = null;
      },
    },
    'collectionPoint.allowedInstallationAction': {
      handler() {
        if (this.collectionPoint.allowedInstallationAction.name === 'READING'
          && this.typesHasEndOfDeliveryType()) {
          this.billingException.type = this.reportReadingTypes.find((type) => type.name === 'END_OF_DELIVERY').name;
        }
      },
    },
  },
  methods: {
    ...mapActions({
      sendBillingExceptionRequest: 'requests/sendBillingExceptionRequest',
      getRequestedLengths: 'requests/getRequestedLengths',
    }),
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year}`;
    },
    save() {
      this.loading = true;

      const requestData = {
        ean: this.collectionPoint.ean,
        billingException: this.billingException,
        file: this.billingExceptionFile,
      };
      this.sendBillingExceptionRequest(requestData).then((response) => {
        this.onInstallationSaveSuccess(response);
      }).finally(() => {
        this.loading = false;
      });
    },
    setStartDate() {
      this.billingException.startDate = this.$dayjs(new Date()).add(1, 'month').startOf('month').format('YYYY-MM-DD');
    },
    setEndDate() {
      const requestLengthName = this.billingExceptionLength
        ? this.billingExceptionLength.name
        : null;
      this.billingException.billingExceptionLength = requestLengthName;
      let endDate = '';
      let startDate = this.$dayjs(new Date()).add(1, 'month').startOf('month').format('YYYY-MM-DD');
      if (this.billingException.startDate) {
        startDate = this.$dayjs(this.billingException.startDate);
      }
      switch (requestLengthName) {
        case 'ONE_MONTH': {
          endDate = startDate.endOf('month');
          break;
        }
        case 'TWO_MONTHS': {
          endDate = startDate.add(1, 'month').endOf('month');
          break;
        }
        case 'THREE_MONTHS': {
          endDate = startDate.add(2, 'month').endOf('month');
          break;
        }
        default: {
          endDate = startDate.endOf('month');
        }
      }
      this.endDate = endDate.format('YYYY-MM-DD');
      let parsedStartDate = null;
      const startDateFormatted = this.formatDate(startDate.format('YYYY-MM-DD'));
      if (startDateFormatted) {
        parsedStartDate = parseDate(startDateFormatted);
      }
      if (parsedStartDate) {
        if (!this.$dayjs(parsedStartDate).isBefore(this.endDate)) {
          this.billingException.startDate = this.endDate;
        }
      }
    },
    fileUpload(event) {
      this.billingExceptionFile = event;
    },
    getAllowedDatesForBillingException(val) {
      return val.endsWith('01');
    },
  },
  computed: {
    minForStartDateFormatted() {
      return this.$dayjs(new Date()).add(1, 'month').startOf('month').format('YYYY-MM-DD');
    },
    maxForStartDateFormatted() {
      return this.$dayjs(new Date()).add(3, 'month').startOf('month').format('YYYY-MM-DD');
    },
  },
  created() {
    this.requestedLengthsLoading = true;
    this.datePickerLoading = true;
    this.setStartDate();
    this.loadInstallation().then((response) => {
      this.redirectByVoltageLevel(response.data, 'NN');
    });
    this.getRequestedLengths().then((response) => {
      this.requestedLengths = response.data;
      this.billingExceptionLength = response.data.find((billingExceptionLength) => billingExceptionLength.name === 'THREE_MONTHS');
      if (this.billingExceptionLength) {
        this.billingException.billingExceptionLength = this.billingExceptionLength.name;
      }
      this.setEndDate();
      this.requestedLengthsLoading = false;
      this.datePickerLoading = false;
    });
  },
};
</script>
